import { useCallback, useEffect, useState } from 'react';
import type { AppSession, PatientView, RewardsView } from '../../lib/types';
import { ApiError, api } from '../../lib/api';
import { fmtAgo, fmtCoins } from '../../lib/format';
import { Icon } from '../../components/HeartMark';
import { Card, Progress } from '../../components/Bits';

/** HeartCoins: the balance and level, how they are earned, what they unlock, and where they went. */
export function RewardsTab({ session, view, reload, say }: { session: AppSession; view: PatientView; reload: () => Promise<void>; say: (s: string) => void }) {
  const [rewards, setRewards] = useState<RewardsView | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const load = useCallback(async () => {
    try {
      setRewards(await api.rewards(session.token));
    } catch (e) {
      say(e instanceof ApiError ? e.message : 'Could not load rewards.');
    }
  }, [session.token, say]);
  useEffect(() => {
    void load();
  }, [load]);

  const coins = rewards?.coins ?? view.coins;
  const level = coins.level;
  const redeem = async (id: string, label: string) => {
    setBusy(id);
    try {
      const r = await api.redeem(id, session.token);
      say(r.pending ? `${label}: redeemed. The coins are on their way out of your treasury.` : `${label}: redeemed.`);
      await load();
      await reload();
    } catch (e) {
      say(e instanceof ApiError ? e.message : 'Could not redeem that.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <section className="banner banner-gold">
        <div className="banner-main">
          <span className="eyebrow">HeartCoins</span>
          <h1>{fmtCoins(coins.balance)}</h1>
          <p className="sub">
            {coins.lifetime === coins.balance ? 'earned so far' : `${fmtCoins(coins.lifetime)} earned in total`} · +{fmtCoins(coins.earnedThisWeek)} this week
          </p>
          <div className="level-bar">
            <span className="pill pill-gold">{level.level}</span>
            <span className="row-main">
              <span className="hint small">{level.next ? `${fmtCoins(level.toNext)} coins to ${level.next}` : 'Top level reached'}</span>
              <Progress value={level.progress * 100} tone="gold" />
            </span>
            {level.next ? <span className="pill pill-soft">{level.next}</span> : null}
          </div>
        </div>
        <div className="tile-row" style={{ minWidth: '260px' }}>
          <div className="tile">
            <strong className="tile-value">🔥 {view.streak.current}</strong>
            <span className="tile-label">Day streak</span>
          </div>
          <div className="tile">
            <strong className="tile-value">{coins.leaderboardRank ? `#${coins.leaderboardRank}` : '—'}</strong>
            <span className="tile-label">Leaderboard</span>
          </div>
        </div>
      </section>

      <div className="grid-2">
        <div className="stack">
          <Card>
            <div className="card-head">
              <h2>Earn HeartCoins</h2>
              <span className="hint">Every healthy habit counts, once a day</span>
            </div>
            {(rewards?.earning ?? []).map((e) => (
              <div className="row earn-row" key={e.reason}>
                <span className={`bubble bubble-sm ${e.doneToday ? 'bubble-green' : 'bubble-soft'}`}>
                  <Icon name={e.reason === 'log-bp' ? 'star' : e.reason === 'steps-goal' ? 'steps' : e.reason === 'all-meds' ? 'pill' : e.reason === 'coach-checkin' ? 'chat' : 'watch'} size={16} />
                </span>
                <span className="row-main">
                  <span className="row-title">{e.label}</span>
                  {e.doneToday ? <span className="row-sub">Done today</span> : null}
                </span>
                <span className={`pill ${e.doneToday ? 'pill-gold' : 'pill-soft'}`}>+{e.coins}</span>
                {e.doneToday ? (
                  <span className="tick on">
                    <Icon name="check" size={12} />
                  </span>
                ) : null}
              </div>
            ))}
          </Card>

          <Card>
            <h2>Achievements</h2>
            <div className="achievements">
              {(rewards?.achievements ?? []).map((a) => (
                <div key={a.id} className={`achievement ${a.earned ? 'earned' : ''}`} title={a.detail}>
                  <span className="bubble bubble-sm">{a.earned ? <Icon name="star" size={16} /> : <Icon name="target" size={16} />}</span>
                  <span>{a.label}</span>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <div className="stack">
          <Card>
            <h2>Redeem</h2>
            <p className="hint">
              {coins.settlement.mode === 'treasury-transfer'
                ? 'Redemptions move coins out of your own treasury, under the authority you signed at your Home.'
                : 'Play coins on this deployment: redemptions are recorded here and settle nowhere else.'}
            </p>
            {(rewards?.catalog ?? []).map((item) => (
              <div className="row" key={item.id}>
                <span className="row-main">
                  <span className="row-title">{item.label}</span>
                  <span className="row-sub">{item.detail}</span>
                </span>
                <button type="button" className="secondary small" disabled={busy !== null || coins.balance < item.coins} onClick={() => void redeem(item.id, item.label)}>
                  {busy === item.id ? '…' : `${fmtCoins(item.coins)}`}
                </button>
              </div>
            ))}
          </Card>

          {rewards && rewards.ledger.length > 0 ? (
            <Card>
              <h2>Coin history</h2>
              <ul className="ledger">
                {rewards.ledger.slice(0, 25).map((r) => (
                  <li key={r.id}>
                    <span className={r.coins >= 0 ? 'good' : 'warn'}>
                      {r.coins >= 0 ? '+' : ''}
                      {fmtCoins(r.coins)}
                    </span>
                    <span className="row-main">
                      <span>{r.reason}</span>
                      <span className="hint small">
                        {fmtAgo(r.at, view.now)}
                        {r.receipt?.status === 'pending' ? ' · settling' : r.receipt?.status === 'failed' ? ` · failed: ${r.receipt.error}` : r.receipt?.ref && r.receipt.mode !== 'play-coins' ? ` · ${r.receipt.ref.slice(0, 10)}…` : ''}
                      </span>
                    </span>
                  </li>
                ))}
              </ul>
            </Card>
          ) : null}
        </div>
      </div>
    </>
  );
}
